import React, { useCallback, useEffect, useRef, useState } from 'react'

import {
	ArrowLeft,
	MoreVertical,
	Pause,
	Pencil,
	Play,
	Trash2,
	X,
} from 'lucide-react'
import type { UseFormReturn } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'

import coverImageBlack from '@/assets/images/logo_isotipo_black.png'
import coverImageWhite from '@/assets/images/logo_isotipo_white.png'
import type { Advert } from '@/core/api/entities/campaign/advert'
import { useTranslation } from '@/core/i18n/useTranslation'
import type { RootState } from '@/core/store'
import {
	resetChatbaseLauncherBottomOffset,
	setChatbaseLauncherBottomOffset,
} from '@/shared/analytics/ChatbaseWidget'
import { Button } from '@/shared/components/atoms/Button'
import Icon from '@/shared/components/atoms/Icon'
import { Separator } from '@/shared/components/atoms/Separator'
import {
	BottomSheet,
	BottomSheetContent,
	BottomSheetTitle,
} from '@/shared/components/molecules/BottomSheet/BottomSheet'
import AuthenticatedLayout from '@/shared/components/organisms/Layout/AuthenticatedLayout/AuthenticatedLayout'
import { shouldRenderMobileDrawerMenu } from '@/shared/components/organisms/Layout/MobileDrawerMenu/MobileDrawerMenu'
import { DesktopVolumeControl } from '@/shared/components/organisms/Player/DesktopVolumeControl'
import Player from '@/shared/components/organisms/Player/Player'
import { useScrollDirection } from '@/shared/hooks/useScrollDirection'
import { cn } from '@/shared/utils/helpers'

import { AssignCampaignModal } from './components/AssignCampaignModal'
import { CampaignConfigForm } from './components/CampaignConfigForm'
import { ConfigurationPreview } from './components/ConfigurationPreview'
import { RemoveCampaignModal } from './components/RemoveCampaignModal'
import type { CampaignConfigFormValues } from './schemas/createCampaignSchema'

interface CampaignDetailsPageUIProps {
	heading: string
	isEdit: boolean
	isLoading: boolean
	form: UseFormReturn<CampaignConfigFormValues>
	currentRRule: string | null
	exdate: string[]
	rruleType: CampaignConfigFormValues['rruleType']
	startDate?: Date
	endDate?: Date
	onSubmit: (values: CampaignConfigFormValues) => void
	onCancel: () => void
	onHeadingClick?: () => void
	readOnly?: boolean
	canEditCampaign?: boolean
	canDeleteCampaign?: boolean
	campaignId?: number
	initialGroupIds?: number[]
}

export const CampaignDetailsPageUI: React.FC<CampaignDetailsPageUIProps> = ({
	heading,
	isEdit,
	isLoading,
	form,
	currentRRule,
	exdate,
	rruleType,
	startDate,
	endDate,
	onSubmit,
	onCancel,
	onHeadingClick,
	readOnly = false,
	canEditCampaign = false,
	canDeleteCampaign = false,
	campaignId,
	initialGroupIds = [],
}) => {
	const { t } = useTranslation('campaigns')
	const navigate = useNavigate()
	const location = useLocation()
	const adverts = useSelector<RootState, Advert[]>(
		(state) => state.campaignFlow.adverts
	)
	const theme = useSelector<RootState, RootState['ui']['theme']>(
		(state) => state.ui.theme
	)
	const playerCoverImage = theme === 'light' ? coverImageBlack : coverImageWhite
	const [isActionsSheetOpen, setIsActionsSheetOpen] = useState(false)
	const [isAssignModalOpen, setIsAssignModalOpen] = useState(false)
	const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false)
	const [activeAdvertId, setActiveAdvertId] = useState<number | null>(null)
	const [currentAdvert, setCurrentAdvert] = useState<Advert | null>(null)
	const [isPlayerPlaying, setIsPlayerPlaying] = useState(false)
	const audioElementRef = useRef<HTMLAudioElement | null>(null)
	const [isMuted, setIsMuted] = useState(false)
	const [volume, setVolume] = useState(1)

	const [isPlayerBarMounted, setIsPlayerBarMounted] = useState(false)
	const [isPlayerBarVisible, setIsPlayerBarVisible] = useState(false)
	const [displayedAdvert, setDisplayedAdvert] = useState<Advert | null>(null)
	const isMobileNavVisible = useScrollDirection({
		threshold: 8,
		topThreshold: 16,
	})
	const shouldShowMobileDrawer = shouldRenderMobileDrawerMenu(location.pathname)
	const shouldOffsetForMobileDrawer =
		shouldShowMobileDrawer && isMobileNavVisible

	const handleAudioRef = useCallback((element: HTMLAudioElement | null) => {
		audioElementRef.current = element
	}, [])

	// Animate bottom player bar in/out (slide from bottom), similar feel to /music.
	useEffect(() => {
		let rafId: number | null = null
		if (readOnly && currentAdvert) {
			setDisplayedAdvert(currentAdvert)
			setIsPlayerBarMounted(true)
			// next paint so transition triggers
			rafId = window.requestAnimationFrame(() => setIsPlayerBarVisible(true))
			return () => {
				if (rafId !== null) {
					window.cancelAnimationFrame(rafId)
				}
			}
		}

		setIsPlayerBarVisible(false)
		const timerId = window.setTimeout(() => {
			setIsPlayerBarMounted(false)
			setDisplayedAdvert(null)
		}, 300)
		return () => window.clearTimeout(timerId)
	}, [readOnly, currentAdvert])

	// Prevent overlap with Chatbase launcher when bottom player is visible.
	// Chatbase can inject its launcher asynchronously, so we retry briefly.
	useEffect(() => {
		if (readOnly && currentAdvert) {
			setChatbaseLauncherBottomOffset(140)
			const started = Date.now()
			const int = window.setInterval(() => {
				setChatbaseLauncherBottomOffset(140)
				if (Date.now() - started > 3000) window.clearInterval(int)
			}, 250)
			return () => {
				window.clearInterval(int)
				resetChatbaseLauncherBottomOffset()
			}
		}

		resetChatbaseLauncherBottomOffset()
	}, [readOnly, currentAdvert])

	useEffect(() => {
		const audioElement = audioElementRef.current
		if (!audioElement) return

		audioElement.volume = isMuted ? 0 : volume
	}, [isMuted, volume])

	useEffect(() => {
		const audioElement = audioElementRef.current
		const isCurrentAdvertDisplayed =
			currentAdvert?.id !== undefined &&
			displayedAdvert?.id !== undefined &&
			currentAdvert.id === displayedAdvert.id

		if (!audioElement || !isCurrentAdvertDisplayed) return

		if (isPlayerPlaying && audioElement.paused) {
			audioElement.play().catch(() => {})
		}
	}, [currentAdvert?.id, displayedAdvert?.id, isPlayerPlaying])
	const handleAdvertRowClick = (advert: Advert) => {
		const isCurrentAdvert = currentAdvert?.id === advert.id
		if (isCurrentAdvert) {
			const audioElement = audioElementRef.current
			if (audioElement?.paused) {
				audioElement.play().catch(() => {})
			} else {
				audioElement?.pause()
			}
			return
		}
		setActiveAdvertId(advert.id)
		setCurrentAdvert(advert)
		setIsPlayerPlaying(true)
	}

	const renderPlayButton = (advert: Advert) => {
		const isCurrent = currentAdvert?.id === advert.id

		if (isCurrent && isPlayerPlaying) {
			return (
				<Button
					variant="ghost"
					size="icon"
					className="h-8 w-8 hover:bg-accent shrink-0"
					onClick={() => audioElementRef.current?.pause()}
				>
					<Pause className="h-4 w-4" />
				</Button>
			)
		}

		return (
			<Button
				variant="ghost"
				size="icon"
				className="h-8 w-8 hover:bg-accent shrink-0"
				onClick={() => {
					if (isCurrent) {
						audioElementRef.current?.play().catch(() => {})
						setIsPlayerPlaying(true)
						return
					}
					setActiveAdvertId(advert.id)
					setCurrentAdvert(advert)
					setIsPlayerPlaying(true)
				}}
			>
				<Play className="h-4 w-4" />
			</Button>
		)
	}

	const handleClosePlayer = () => {
		audioElementRef.current?.pause()
		setCurrentAdvert(null)
		setActiveAdvertId(null)
		setIsPlayerPlaying(false)
	}

	const renderAdvertDateRange = (advert: Advert) => {
		const startLabel = advert.dateStart
			? advert.dateStart.toFormat('MMM d, yyyy')
			: t('upload.noStart')
		const endLabel = advert.dateEnd
			? advert.dateEnd.toFormat('MMM d, yyyy')
			: t('upload.noEnd')
		return `${startLabel} – ${endLabel}`
	}

	const handleManageAssignment = () => {
		if (!campaignId) return
		setIsActionsSheetOpen(false)
		setIsAssignModalOpen(true)
	}

	const handleChangeInfo = () => {
		if (!campaignId) return
		setIsActionsSheetOpen(false)
		navigate(`/campaigns/${campaignId}/edit`)
	}

	const handleRemove = () => {
		if (!campaignId) return
		setIsActionsSheetOpen(false)
		setIsRemoveModalOpen(true)
	}

	return (
		<AuthenticatedLayout requireAuth={true}>
			<div
				className={`w-full pl-0 md:pl-3 pr-0 md:pr-3 2xl:pl-6 2xl:pr-6 ${
					readOnly
						? 'md:py-3 2xl:py-6'
						: 'fixed inset-0 z-40 bg-black/30 flex items-end md:static md:z-auto md:bg-transparent md:py-3 2xl:py-6'
				}`}
			>
				<div className="w-full max-w-none md:max-w-[1400px] md:mx-auto">
					<div
						className={cn(
							'bg-card w-full border-border overflow-hidden flex flex-col min-h-0',
							readOnly
								? 'h-auto md:border md:rounded-xl'
								: 'h-[92dvh] rounded-t-2xl border-t md:h-auto md:border md:rounded-xl',
							readOnly &&
								isPlayerBarMounted &&
								'pb-[96px] md:pb-[120px] lg:pb-[132px]'
						)}
					>
						<div
							className={`md:hidden flex justify-center pt-3 pb-2 ${readOnly ? 'hidden' : ''}`}
						>
							<div className="w-10 h-1 bg-gray-600 rounded-full" />
						</div>
						<div className="hidden md:flex py-6 px-8 items-center gap-4 justify-between">
							<div className="flex items-center gap-4">
								<button
									onClick={() => navigate('/campaigns')}
									className="flex items-center justify-center"
								>
									<ArrowLeft className="w-6 h-6" />
								</button>
								<h2
									className="text-lg font-normal tracking-tight md:text-3xl"
									onClick={onHeadingClick}
								>
									{heading}
								</h2>
							</div>
							{readOnly && campaignId && canEditCampaign && (
								<Button
									variant="secondary"
									className="gap-2"
									onClick={() => navigate(`/campaigns/${campaignId}/edit`)}
								>
									<Pencil className="h-4 w-4" />
									{t('config.editTitle')}
								</Button>
							)}
						</div>
						<div
							className={`md:hidden flex items-center justify-between ${
								readOnly ? 'py-3 px-5' : 'py-3 px-4'
							}`}
						>
							<div className="flex items-center gap-2 min-w-0">
								{readOnly && (
									<button
										type="button"
										onClick={() => navigate('/campaigns')}
										className="h-9 w-9 flex items-center justify-center rounded-full hover:bg-muted/80 transition-colors shrink-0"
									>
										<ArrowLeft className="w-5 h-5" />
									</button>
								)}
								<h2 className="text-lg font-semibold tracking-tight truncate">
									{heading}
								</h2>
							</div>
							<div className="flex items-center gap-2">
								{readOnly ? (
									<button
										type="button"
										onClick={() => setIsActionsSheetOpen(true)}
										className="h-10 w-10 flex items-center justify-center rounded-full border border-border bg-muted/30 hover:bg-muted/60 transition-colors"
									>
										<MoreVertical className="w-5 h-5" />
									</button>
								) : (
									<>
										{!isEdit && !readOnly && (
											<div className="rounded-lg bg-muted px-2.5 py-1 text-base font-medium">
												1/2
											</div>
										)}
										<button
											type="button"
											onClick={onCancel}
											className="h-8 w-8 flex items-center justify-center rounded-full hover:bg-muted/80 transition-colors"
										>
											<X className="w-5 h-5" />
										</button>
									</>
								)}
							</div>
						</div>

						<Separator />

						<div className="px-4 md:px-8 py-4 md:py-6 flex-1 flex gap-8 overflow-y-auto">
							<div
								className={`flex-1 min-w-0 ${readOnly ? '' : 'pb-24 md:pb-0'}`}
							>
								{isLoading ? (
									<div className="text-muted-foreground text-sm">
										{t('config.loading')}
									</div>
								) : (
									<div className="relative">
										<CampaignConfigForm
											isEdit={isEdit}
											form={form}
											onSubmit={onSubmit}
											onCancel={onCancel}
											submitLabel={t('config.continue')}
											isSubmitting={false}
											readOnly={readOnly}
											onReadOnlyMobileAdvertClick={
												readOnly ? handleAdvertRowClick : undefined
											}
											activeAdvertId={readOnly ? activeAdvertId : null}
											isActiveAdvertPlaying={readOnly ? isPlayerPlaying : false}
										/>
										{readOnly && (
											<div
												className="absolute inset-0 z-10 hidden md:block"
												aria-hidden="true"
											/>
										)}
									</div>
								)}
							</div>

							<div className="hidden md:block w-[380px] min-h-[260px] border-l pl-8">
								{readOnly ? (
									<>
										<h3 className="text-sm font-medium mb-4">
											{t('upload.selectedAds')}
										</h3>
										{adverts.length > 0 ? (
											<ul className="divide-y text-sm max-h-[90vh] md:border md:rounded-md pr-2 md:pr-0 md:bg-muted/50 overflow-y-auto">
												{adverts.map((advert) => (
													<li
														key={advert.id}
														className="flex items-center justify-between px-3 py-2 bg-card"
													>
														<div className="flex items-center gap-3 min-w-0">
															{renderPlayButton(advert)}
															<div className="flex flex-col min-w-0">
																<span className="font-medium truncate max-w-[220px]">
																	{advert.name}
																</span>
																<span className="text-[11px] text-muted-foreground">
																	{renderAdvertDateRange(advert)}
																</span>
															</div>
														</div>
														<span className="text-[11px] text-muted-foreground">
															ID {advert.id}
														</span>
													</li>
												))}
											</ul>
										) : (
											<p className="text-xs text-muted-foreground">
												{t('upload.noAdsAssigned')}
											</p>
										)}
									</>
								) : (
									<ConfigurationPreview
										rrule={currentRRule}
										exdate={exdate}
										timeFrom={
											rruleType === 'recurrent'
												? form.watch('timeFrom')
												: undefined
										}
										startDate={startDate}
										endDate={endDate}
									/>
								)}
							</div>
						</div>
					</div>
				</div>
			</div>
			{isPlayerBarMounted && displayedAdvert && (
				<div
					className={cn(
						'fixed md:bottom-0 left-0 right-0 lg:left-20 z-[1000] transition-transform duration-300 ease-out',
						shouldOffsetForMobileDrawer ? 'bottom-23' : 'bottom-0',
						isPlayerBarVisible ? 'translate-y-0' : 'translate-y-full'
					)}
				>
					<div className="w-full 2xl:px-6 md:px-3">
						<div className="mx-auto w-full max-w-[1400px]">
							<div className="player-mobile-shell dark:md:bg-card md:border md:bg-secondary lg:rounded-b-[8px] px-4 py-3 md:px-6 md:py-4 lg:py-6 flex items-center lg:h-[120px]">
								<div className="w-full flex items-center gap-4">
									<Player
										key={displayedAdvert.id}
										src={displayedAdvert.path}
										cover={playerCoverImage}
										title={displayedAdvert.name || ''}
										metadata={[heading].filter(Boolean)}
										autoPlay={true}
										onPlay={() => setIsPlayerPlaying(true)}
										onPause={() => setIsPlayerPlaying(false)}
										onEnded={() => setIsPlayerPlaying(false)}
										hideVolume={true}
										compact={true}
										showSkipControls={false}
										audioRef={handleAudioRef}
										cssVars={{
											border: 'none',
											backgroundColor: 'transparent',
										}}
									/>

									<div className="lg:flex hidden items-center gap-6 shrink-0">
										<DesktopVolumeControl
											isMuted={isMuted}
											setIsMuted={setIsMuted}
											volume={volume}
											setVolume={setVolume}
											audioElementRef={audioElementRef}
										/>
										<div aria-hidden="true" className="w-[56px] shrink-0" />
									</div>
									<button
										className="absolute top-1 right-1 md:top-2 md:right-4 rounded-[9999px] cursor-pointer p-2 flex items-center justify-center shrink-0"
										onClick={handleClosePlayer}
										aria-label={t('common:actions.close', 'Close')}
									>
										<Icon
											name="close"
											width="16px"
											height="16px"
											color="var(--foreground)"
										/>
									</button>
								</div>
							</div>
						</div>
					</div>
				</div>
			)}

			<BottomSheet
				open={isActionsSheetOpen}
				onOpenChange={setIsActionsSheetOpen}
			>
				<BottomSheetContent
					variant="card"
					className="border-border h-auto max-h-[90vh] px-4 pb-6 pt-0"
					aria-describedby={undefined}
				>
					<BottomSheetTitle className="sr-only">{heading}</BottomSheetTitle>
					<div className="space-y-3 pt-2">
						<Button
							type="button"
							onClick={handleManageAssignment}
							className="w-full rounded-full h-14 text-base font-medium"
						>
							{t('list.actions.manageAssignment')}
						</Button>
						{canEditCampaign && (
							<Button
								type="button"
								variant="outline"
								onClick={handleChangeInfo}
								className="w-full rounded-full h-14 text-base font-medium"
							>
								<Pencil className="w-4 h-4 mr-2" />
								{t('config.mobile.changeInfo')}
							</Button>
						)}
						{canDeleteCampaign && (
							<Button
								type="button"
								variant="outline"
								onClick={handleRemove}
								className="w-full rounded-full h-14 text-base font-medium text-destructive border-destructive/30 hover:text-destructive"
							>
								<Trash2 className="w-4 h-4 mr-2 text-destructive" />
								{t('list.actions.delete')}
							</Button>
						)}
					</div>
				</BottomSheetContent>
			</BottomSheet>

			<AssignCampaignModal
				open={isAssignModalOpen}
				onOpenChange={setIsAssignModalOpen}
				campaignId={campaignId}
				campaignName={heading}
				initialGroupIds={initialGroupIds}
			/>

			<RemoveCampaignModal
				open={isRemoveModalOpen}
				onOpenChange={setIsRemoveModalOpen}
				campaignId={campaignId}
				campaignName={heading}
			/>
		</AuthenticatedLayout>
	)
}

export default CampaignDetailsPageUI
